// features/public-filters/domain/mappers/lotSizeMapper.ts
import { Range } from '../FilterTypes';

const SQFT_PER_ACRE = 43560;

/**
 * Parse a lot size display string into square feet
 * @param value - Display string like "2,000 sqft", ".25 acres", "No min", etc.
 * @returns Numeric value in sqft or null for unbounded
 */
export function parseLotSizeValue(value: string): number | null {
  if (value === 'No min' || value === 'No max') return null;

  // Handle sqft values (e.g., "2,000 sqft" -> 2000)
  if (value.includes('sqft')) {
    const match = value.match(/(\d{1,3}(?:,\d{3})*)/);
    if (match) {
      return parseInt(match[1].replace(/,/g, ''));
    }
  }

  // Handle acre values (e.g., ".25 acres" -> 10890, "1 acre" -> 43560)
  if (value.includes('acre')) {
    const match = value.match(/(\d*\.?\d+)/);
    if (match) {
      const acres = parseFloat(match[1]);
      return Math.round(acres * SQFT_PER_ACRE);
    }
  }

  return null;
}

/**
 * Format a lot size numeric value (in sqft) for display
 * @param value - Numeric value in sqft or null
 * @param isMin - Whether this is a min or max value
 * @returns Display string like "1 acre", "2,000 sqft", etc.
 */
export function formatLotSizeValue(value: number | null, isMin: boolean): string {
  if (value === null) return isMin ? 'No min' : 'No max';

  // If it's a round acre value, show as acres
  const acres = value / SQFT_PER_ACRE;
  if (acres >= 1 && Number.isInteger(acres)) {
    return `${acres} acre${acres > 1 ? 's' : ''}`;
  }

  // Check for common fractional acres
  if (acres === 0.25) return '.25 acres';
  if (acres === 0.5) return '.5 acres';
  if (acres === 0.75) return '.75 acres';

  // Otherwise show as sqft with commas
  return `${value.toLocaleString()} sqft`;
}

/**
 * Apply lot size change from dropdown selection
 * @param currentRange - Current lot size range
 * @param type - Whether changing min or max
 * @param value - New display value from dropdown
 * @returns New Range with updated value
 */
export function applyLotSizeChange(
  currentRange: Range,
  type: 'min' | 'max',
  value: string,
): Range {
  const numericValue = parseLotSizeValue(value);

  if (type === 'min') {
    return Range.create(numericValue, currentRange.max);
  } else {
    return Range.create(currentRange.min, numericValue);
  }
}
